import { z } from 'zod'
import { CUSTOM_THEME, HUES, THEMES } from '@/lib/colour'

// The one Settings resource (spec §10). Well-known id shared with the server.
/** settings.DefaultAgentDailyUSD */
export const AGENT_DAILY_USD = 20

export const SETTINGS_ID = '00000000-0000-7000-8000-000000000001'

/** label that marks an admin member's own Settings record; its value is her grant id (settings.MemberLabel) */
export const MEMBER_LABEL = 'member'

/** The keys an admin member keeps on her own record (settings.MemberKeys): her look and layout, not the owner's server settings. */
export const MEMBER_SETTINGS_KEYS = [
  'person_look',
  'phone',
  'phones',
  'widgets',
  'app_icon',
  'label_styles',
  'today_cap',
  'split_threshold',
  'stale_inbox_days',
  'stale_next_days',
  'triage_inbox_at',
  'review_hint',
] as const

export const settingsSpec = z
  .object({
    today_cap: z.number().int().min(1).max(20).default(3),
    split_threshold: z.number().int().min(1).default(45),
    stale_inbox_days: z.number().int().min(1).default(7),
    /** how many things may sit in the inbox before the assistant offers to sort them; 0 = never */
    triage_inbox_at: z.number().int().min(0).max(100).default(5),
    assistant_daily_tokens: z.number().int().min(0).default(0),
    stale_next_days: z.number().int().min(1).default(14),
    daily_push: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/)
      .nullable()
      .default(null),
    /** local HH:MM of the evening recap push; null for none */
    recap_push: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/)
      .nullable()
      .default(null),
    /** local HH:MM on Sundays of the week's plan push (/plan?for=week); null for none */
    week_push: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/)
      .nullable()
      .default(null),
    /** local HH:MM on Fridays of the weekend's plan push (/plan?for=weekend); null for none */
    weekend_push: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/)
      .nullable()
      .default(null),
    /** local HH:MM of the nightly check-in push; null for none */
    checkin_push: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/)
      .nullable()
      .default(null),
    /** minutes between time anchors after "End of day"; 0 = none */
    anchor_minutes: z.number().int().min(0).max(240).default(45),
    /** the unit of the smoking log, in the owner's own words */
    smoke_unit: z.string().max(40).default('smoke'),
    review_hint: z.boolean().default(true),
    /** push "Added: …" to other devices when something new lands */
    notify_on_add: z.boolean().default(false),
    /** the assistant files each capture (annotate, schedule, project) and pushes a receipt */
    triage_captures: z.boolean().default(false),
    /** an agent session does what it proposes instead of asking; ⌘Z takes a turn back */
    agent_acts: z.boolean().default(false),
    /** Prime looks after the coding agents: woken when one finishes, reviews it, tells it what to fix (unset = on) */
    prime_watches_agents: z.boolean().nullable().optional(),
    /** USD a day Prime may spend on agents it starts itself (prototypes); unset is AGENT_DAILY_USD, 0 no cap */
    agent_daily_usd: z.number().min(0).nullable().optional(),
    label_styles: z
      .record(z.string(), z.object({ colour: z.string().optional(), icon: z.string().optional() }).strict())
      .default({}),
    /** addresses or CIDRs refused on every path unless the request carries the master token */
    blocked_ips: z.array(z.string().max(64)).max(500).default([]),
    /** minutes of "get ready" before leaving for an appointment (or of heads-up before one with no travel); unset is 10, 0 none */
    ready_minutes: z.number().int().min(0).max(120).optional(),
    /** lead days per Date category, 0–90; a category left out keeps its default */
    date_leads: z.record(z.string(), z.number().int().min(0).max(90)).optional(),
    /** the phone's home screens, dock and launcher; shaped and repaired by os/layout.ts */
    phone: z.record(z.string(), z.unknown()).optional(),
    /** each suite's own phone arrangement, keyed by workspace slug; a suite with none uses `phone` */
    phones: z
      .record(
        z.string(),
        z
          .record(z.string(), z.unknown())
          .refine((l) => Object.keys(l).length <= 16, 'a layout has at most 16 keys'),
      )
      .refine((p) => Object.keys(p).length <= 32, 'at most 32 suites')
      .optional(),
    /** queries pinned as tabs on the All page (lib/allItems.ts) */
    all_tabs: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z0-9]{1,12}$/),
            name: z.string().min(1).max(40),
            q: z.string().max(300),
          })
          .strict(),
      )
      .max(12)
      .optional(),
    /** Learn: the daily deck's push (local HH:MM, 09:30 unless set; push_off turns it off) and the quick pick's timer in seconds (15 unless set, 0 is none) */
    learn: z
      .object({
        push_at: z
          .string()
          .regex(/^[0-2][0-9]:[0-5][0-9]$/)
          .optional(),
        push_off: z.boolean().optional(),
        timer_s: z.number().int().min(0).max(120).optional(),
        /** the last "Test me" setup: scope (all, module:<slug>, pick, weakest), pick, length (0 is endless), timer */
        test: z
          .object({
            scope: z.string().max(40).optional(),
            patterns: z.array(z.string().max(60)).max(40).optional(),
            length: z.number().int().min(0).max(100).optional(),
            timer: z.boolean().optional(),
          })
          .optional(),
      })
      .strict()
      .optional(),
    /** what the outside widgets need: the weather's place and units, the house widget's entities (os/widgetConfig.ts) */
    widgets: z
      .object({
        place: z
          .object({
            name: z.string().max(120),
            latitude: z.number().min(-90).max(90),
            longitude: z.number().min(-180).max(180),
          })
          .strict()
          .optional(),
        units: z.enum(['celsius', 'fahrenheit']).optional(),
        house: z.array(z.string().max(120)).max(60).optional(),
      })
      .strict()
      .optional(),
    /** Everything's own theme, palette and accent (scope `*` has no Workspace to hold them); the same three fields a suite carries */
    look: z
      .object({
        theme: z.enum([...THEMES, CUSTOM_THEME]).optional(),
        palette: z.record(z.string(), z.string().max(64)).optional(),
        colour: z.enum(HUES).optional(),
        brightness: z.enum(['auto', 'light', 'dark', 'black']).optional(),
        glass: z.boolean().optional(),
      })
      .strict()
      .optional(),
    /** an admin member's own look, one versioned object that can grow (lib/look.ts `PersonLook`); the owner's Workspace records and `look` stay his */
    person_look: z.record(z.string(), z.unknown()).optional(),
    /** home-screen icon: drawn on the device, so it works offline and per install */
    app_icon: z
      .object({
        style: z.enum(['horizon', 'sunrise', 'arc', 'ring', 'dot', 'check', 'bolt', 'letter', 'os']),
        colour: z.string(),
        bg: z.enum(['sky', 'night', 'black', 'ink', 'accent', 'paper']),
      })
      .strict()
      .optional(),
  })
  .strict()
export type SettingsSpec = z.infer<typeof settingsSpec>

export const settingsDefaults = (spec: Partial<SettingsSpec> = {}): SettingsSpec => ({
  today_cap: 3,
  split_threshold: 45,
  stale_inbox_days: 7,
  triage_inbox_at: 5,
  assistant_daily_tokens: 0,
  stale_next_days: 14,
  daily_push: null,
  recap_push: null,
  week_push: null,
  weekend_push: null,
  checkin_push: null,
  anchor_minutes: 45,
  smoke_unit: 'smoke',
  review_hint: true,
  notify_on_add: false,
  triage_captures: false,
  agent_acts: false,
  label_styles: {},
  blocked_ips: [],
  ...spec,
})
